import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Search, PenTool, Target, Lightbulb, Rocket, Sparkles,
    Loader2, CheckCircle, Send, RefreshCw, X, ChevronDown,
    Video, Zap, AlertCircle, Crown, ArrowRight, Bot, User,
    Copy, Check, History, MessageSquare, Plus, Trash2, ChevronLeft, ChevronRight, Brain,
    FileText, Settings
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    fetchSkillsByCategory,
    sendMarketingMessage,
    generateSessionId,
    hasMarketingAccess,
    fetchMarketingSessions,
    loadMarketingSession,
    deleteMarketingSession,
    CATEGORY_META
} from '../services/MarketingService';
import MarketingBriefEditor from './MarketingBriefEditor';
import {
    fetchCreditBalance,
    CREDITS_PER_MARKETING_CHAT,
    CREDIT_THRESHOLDS,
    getCreditStatusColor,
    formatCredits,
    getEstimatedChatsRemaining
} from '../services/ChatUsageService';
import AIReasoningPanel from './shared/AIReasoningPanel';

// Fallback skills for when API is unavailable
const FALLBACK_SKILLS = {
    seo: [
        { id: 'programmatic-seo', name: 'Programmatic SEO', description: 'Scale SEO with automated page generation', category: 'seo' },
        { id: 'seo-audit', name: 'SEO Audit', description: 'Comprehensive site analysis and recommendations', category: 'seo' },
        { id: 'schema-markup', name: 'Schema Markup', description: 'Structured data for rich search results', category: 'seo' }
    ],
    copywriting: [
        { id: 'copywriting', name: 'Copywriting', description: 'Persuasive marketing copy', category: 'copywriting' },
        { id: 'copy-editing', name: 'Copy Editing', description: 'Polish and refine your content', category: 'copywriting' },
        { id: 'email-sequence', name: 'Email Sequence', description: 'Automated email campaigns', category: 'copywriting' },
        { id: 'social-content', name: 'Social Content', description: 'Engaging social media posts', category: 'copywriting' },
        { id: 'landing-pages', name: 'Landing Pages', description: 'High-converting page copy', category: 'copywriting' }
    ],
    cro: [
        { id: 'form-cro', name: 'Form CRO', description: 'Optimize form completion rates', category: 'cro' },
        { id: 'page-cro', name: 'Page CRO', description: 'Improve page conversion rates', category: 'cro' },
        { id: 'popup-cro', name: 'Popup CRO', description: 'Effective popup strategies', category: 'cro' },
        { id: 'signup-flow-cro', name: 'Signup Flow CRO', description: 'Streamline user registration', category: 'cro' },
        { id: 'paywall-upgrade-cro', name: 'Paywall Upgrade CRO', description: 'Increase paid conversions', category: 'cro' },
        { id: 'onboarding-cro', name: 'Onboarding CRO', description: 'Improve user activation', category: 'cro' }
    ],
    strategy: [
        { id: 'pricing-strategy', name: 'Pricing Strategy', description: 'Optimize your pricing model', category: 'strategy' },
        { id: 'launch-strategy', name: 'Launch Strategy', description: 'Go-to-market planning', category: 'strategy' },
        { id: 'free-tool-strategy', name: 'Free Tool Strategy', description: 'Lead magnets and freemium', category: 'strategy' },
        { id: 'marketing-ideas', name: 'Marketing Ideas', description: 'Creative campaign concepts', category: 'strategy' },
        { id: 'competitor-alternatives', name: 'Competitor Analysis', description: 'Competitive positioning', category: 'strategy' }
    ],
    growth: [
        { id: 'referral-program', name: 'Referral Program', description: 'Viral growth mechanics', category: 'growth' },
        { id: 'ab-test-setup', name: 'A/B Test Setup', description: 'Experiment design and analysis', category: 'growth' },
        { id: 'analytics-tracking', name: 'Analytics Tracking', description: 'Measurement and attribution', category: 'growth' },
        { id: 'paid-ads', name: 'Paid Ads', description: 'Paid acquisition campaigns', category: 'growth' },
        { id: 'marketing-psychology', name: 'Marketing Psychology', description: 'Behavioral triggers', category: 'growth' }
    ],
    general: [
        { id: 'high-retention-video-ads', name: 'High-Retention Video Ads', description: 'Engaging video ad formats', category: 'general' },
        { id: 'shorts-best-practice', name: 'Shorts Best Practice', description: 'Short-form video optimization', category: 'general' },
        { id: 'how-it-works', name: 'How It Works Videos', description: 'Explainer video frameworks', category: 'general' }
    ]
};

const CATEGORY_ICONS = {
    seo: Search,
    copywriting: PenTool,
    cro: Target,
    strategy: Lightbulb,
    growth: Rocket,
    general: Video
};

const CATEGORY_NAMES = {
    seo: 'SEO',
    copywriting: 'Copywriting',
    cro: 'CRO',
    strategy: 'Strategy',
    growth: 'Growth',
    general: 'Content'
};

const colorClasses = {
    emerald: {
        bg: 'bg-emerald-500',
        bgLight: 'bg-emerald-500/10',
        border: 'border-emerald-500/30',
        text: 'text-emerald-500',
        ring: 'ring-emerald-500/20',
    },
    violet: {
        bg: 'bg-violet-500',
        bgLight: 'bg-violet-500/10',
        border: 'border-violet-500/30',
        text: 'text-violet-500',
        ring: 'ring-violet-500/20',
    },
    orange: {
        bg: 'bg-orange-500',
        bgLight: 'bg-orange-500/10',
        border: 'border-orange-500/30',
        text: 'text-orange-500',
        ring: 'ring-orange-500/20',
    },
    amber: {
        bg: 'bg-amber-500',
        bgLight: 'bg-amber-500/10',
        border: 'border-amber-500/30',
        text: 'text-amber-500',
        ring: 'ring-amber-500/20',
    },
    sky: {
        bg: 'bg-sky-500',
        bgLight: 'bg-sky-500/10',
        border: 'border-sky-500/30',
        text: 'text-sky-500',
        ring: 'ring-sky-500/20',
    },
    rose: {
        bg: 'bg-rose-500',
        bgLight: 'bg-rose-500/10',
        border: 'border-rose-500/30',
        text: 'text-rose-500',
        ring: 'ring-rose-500/20',
    }
};

const CATEGORY_COLORS = {
    seo: 'emerald',
    copywriting: 'violet',
    cro: 'orange',
    strategy: 'amber',
    growth: 'sky',
    general: 'rose'
};

// Upgrade Prompt Component
const UpgradePrompt = ({ onUpgrade }) => (
    <div className="flex flex-col items-center justify-center h-full bg-[#f7f4ee] dark:bg-slate-950 p-8">
        <div className="max-w-md text-center">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center mx-auto mb-6">
                <Crown className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
                Marketing Assistant
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
                Get AI-powered marketing guidance with 25+ SOPs across SEO, copywriting, CRO, strategy, growth, and content.
            </p>
            <div className="bg-white dark:bg-slate-800 rounded-xl p-4 mb-6 text-left">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Included features:</p>
                <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                    <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        25+ Marketing SOPs
                    </li>
                    <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        Multi-skill analysis
                    </li>
                    <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        AI-powered recommendations
                    </li>
                    <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        Conversation history
                    </li>
                </ul>
            </div>
            <button
                onClick={onUpgrade}
                className="w-full px-6 py-3 bg-gradient-to-r from-violet-500 to-pink-500 text-white font-semibold rounded-xl hover:from-violet-600 hover:to-pink-600 transition-all flex items-center justify-center gap-2"
            >
                Upgrade to Pro
                <ArrowRight className="w-5 h-5" />
            </button>
            <p className="text-xs text-slate-500 mt-3">
                Available on Pro, Ultra, and Enterprise plans
            </p>
        </div>
    </div>
);

const MarketingAgent = ({ userEmail, subscription, onUpgradePlan }) => {
    // Check subscription access
    const hasAccess = hasMarketingAccess(subscription);

    // Skills state
    const [skillsByCategory, setSkillsByCategory] = useState(null);
    const [isLoadingSkills, setIsLoadingSkills] = useState(true);
    const [skillsError, setSkillsError] = useState(null);

    // Selection state
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [isSkillPanelExpanded, setIsSkillPanelExpanded] = useState(false);

    // Chat state
    const [sessionId, setSessionId] = useState(() => generateSessionId([]));
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const [thinkingSteps, setThinkingSteps] = useState([]);
    const [hasResponseStarted, setHasResponseStarted] = useState(false);
    const [streamingResponse, setStreamingResponse] = useState('');
    const [error, setError] = useState(null);
    const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);

    // Credit state
    const [credits, setCredits] = useState(null);
    const [lastCreditDeduction, setLastCreditDeduction] = useState(null);
    const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
    const [insufficientCreditsInfo, setInsufficientCreditsInfo] = useState(null);

    // Chat history state
    const [chatSessions, setChatSessions] = useState([]);
    const [isHistorySidebarOpen, setIsHistorySidebarOpen] = useState(true); // Show by default like ChatGPT
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const [isLoadingSession, setIsLoadingSession] = useState(false);
    const [deletingSessionId, setDeletingSessionId] = useState(null);

    // Brief editor state
    const [isBriefEditorOpen, setIsBriefEditorOpen] = useState(false);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const hasResponseStartedRef = useRef(false);

    // Copy message to clipboard
    const handleCopyMessage = async (index, content) => {
        try {
            await navigator.clipboard.writeText(content);
            setCopiedMessageIndex(index);
            setTimeout(() => setCopiedMessageIndex(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // Simple markdown renderer
    const renderMarkdown = (text) => {
        if (!text) return null;

        // Split by code blocks first
        const codeBlockRegex = /```(\w*)\n?([\s\S]*?)```/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = codeBlockRegex.exec(text)) !== null) {
            if (match.index > lastIndex) {
                parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
            }
            parts.push({ type: 'code', language: match[1], content: match[2] });
            lastIndex = match.index + match[0].length;
        }
        if (lastIndex < text.length) {
            parts.push({ type: 'text', content: text.slice(lastIndex) });
        }

        return (
            <div className="space-y-3">
                {parts.map((part, idx) => {
                    if (part.type === 'code') {
                        return (
                            <pre key={idx} className="bg-slate-900 dark:bg-slate-950 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm font-mono">
                                <code>{part.content.trim()}</code>
                            </pre>
                        );
                    }
                    return <div key={idx}>{renderTextWithMarkdown(part.content)}</div>;
                })}
            </div>
        );
    };

    // Render text with inline markdown
    const renderTextWithMarkdown = (text) => {
        if (!text) return null;

        const lines = text.split('\n');
        const elements = [];
        let currentList = [];
        let listType = null;

        const flushList = () => {
            if (currentList.length > 0) {
                const ListTag = listType === 'ordered' ? 'ol' : 'ul';
                elements.push(
                    <ListTag key={elements.length} className={`${listType === 'ordered' ? 'list-decimal' : 'list-disc'} list-inside space-y-1 my-2`}>
                        {currentList.map((item, i) => (
                            <li key={i} className="text-slate-700 dark:text-slate-300">{renderInlineMarkdown(item)}</li>
                        ))}
                    </ListTag>
                );
                currentList = [];
                listType = null;
            }
        };

        lines.forEach((line, lineIdx) => {
            const trimmedLine = line.trim();

            if (trimmedLine.startsWith('### ')) {
                flushList();
                elements.push(<h3 key={lineIdx} className="text-base font-bold text-slate-900 dark:text-white mt-4 mb-2">{renderInlineMarkdown(trimmedLine.slice(4))}</h3>);
            } else if (trimmedLine.startsWith('## ')) {
                flushList();
                elements.push(<h2 key={lineIdx} className="text-lg font-bold text-slate-900 dark:text-white mt-4 mb-2">{renderInlineMarkdown(trimmedLine.slice(3))}</h2>);
            } else if (trimmedLine.startsWith('# ')) {
                flushList();
                elements.push(<h1 key={lineIdx} className="text-xl font-bold text-slate-900 dark:text-white mt-4 mb-2">{renderInlineMarkdown(trimmedLine.slice(2))}</h1>);
            } else if (trimmedLine.match(/^[-*]\s/)) {
                if (listType !== 'unordered') {
                    flushList();
                    listType = 'unordered';
                }
                currentList.push(trimmedLine.replace(/^[-*]\s/, ''));
            } else if (trimmedLine.match(/^\d+\.\s/)) {
                if (listType !== 'ordered') {
                    flushList();
                    listType = 'ordered';
                }
                currentList.push(trimmedLine.replace(/^\d+\.\s/, ''));
            } else if (trimmedLine === '') {
                flushList();
                elements.push(<div key={lineIdx} className="h-2" />);
            } else {
                flushList();
                elements.push(<p key={lineIdx} className="text-slate-700 dark:text-slate-300 my-1">{renderInlineMarkdown(line)}</p>);
            }
        });

        flushList();
        return <>{elements}</>;
    };

    // Render inline markdown (bold, italic, code)
    const renderInlineMarkdown = (text) => {
        if (!text) return null;

        const parts = [];
        let remaining = text;
        let key = 0;

        while (remaining.length > 0) {
            // Bold
            let match = remaining.match(/^(.*?)\*\*(.+?)\*\*/);
            if (match) {
                if (match[1]) parts.push(<span key={key++}>{match[1]}</span>);
                parts.push(<strong key={key++} className="font-semibold text-slate-900 dark:text-white">{match[2]}</strong>);
                remaining = remaining.slice(match[0].length);
                continue;
            }

            // Inline code
            match = remaining.match(/^(.*?)`(.+?)`/);
            if (match) {
                if (match[1]) parts.push(<span key={key++}>{match[1]}</span>);
                parts.push(<code key={key++} className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-violet-600 dark:text-violet-400 rounded text-sm font-mono">{match[2]}</code>);
                remaining = remaining.slice(match[0].length);
                continue;
            }

            parts.push(<span key={key++}>{remaining}</span>);
            break;
        }

        return <>{parts}</>;
    };

    // Format message content - detect JSON and render nicely
    const formatMessageContent = (content) => {
        if (!content) return null;

        // Try to detect and parse JSON
        const trimmed = content.trim();
        if ((trimmed.startsWith('[') && trimmed.endsWith(']')) ||
            (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
            try {
                const parsed = JSON.parse(trimmed);

                // Handle array of social media posts
                if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].post) {
                    return (
                        <div className="space-y-4">
                            {parsed.map((item, idx) => (
                                <div key={idx} className="bg-slate-50 dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                                    {item.platform && (
                                        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200 dark:border-slate-700">
                                            <span className="text-xs font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wide">
                                                {item.platform}
                                            </span>
                                        </div>
                                    )}
                                    <div className="text-sm whitespace-pre-wrap text-slate-700 dark:text-slate-300">
                                        {item.post || item.content || item.text}
                                    </div>
                                </div>
                            ))}
                        </div>
                    );
                }

                // Handle single object with content
                if (parsed.post || parsed.content || parsed.text) {
                    return (
                        <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                            {parsed.platform && (
                                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200 dark:border-slate-700">
                                    <span className="text-xs font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wide">
                                        {parsed.platform}
                                    </span>
                                </div>
                            )}
                            <div className="text-sm whitespace-pre-wrap text-slate-700 dark:text-slate-300">
                                {parsed.post || parsed.content || parsed.text}
                            </div>
                        </div>
                    );
                }

                // For other JSON, just format it nicely
                return (
                    <pre className="text-sm bg-slate-50 dark:bg-slate-900 p-3 rounded-lg overflow-x-auto border border-slate-200 dark:border-slate-700">
                        <code>{JSON.stringify(parsed, null, 2)}</code>
                    </pre>
                );
            } catch {
                // Not valid JSON, render as markdown
            }
        }

        // Render as markdown
        return renderMarkdown(content);
    };

    // Fetch skills on mount
    useEffect(() => {
        if (!hasAccess) return;

        const loadSkills = async () => {
            setIsLoadingSkills(true);
            setSkillsError(null);
            try {
                const data = await fetchSkillsByCategory();
                setSkillsByCategory(data.categories);
            } catch (err) {
                console.error('Failed to fetch skills:', err);
                // Use fallback skills silently
                setSkillsByCategory(FALLBACK_SKILLS);
            } finally {
                setIsLoadingSkills(false);
            }
        };

        loadSkills();
    }, [hasAccess]);

    // Fetch credit balance on mount
    useEffect(() => {
        if (!hasAccess) return;

        const loadCredits = async () => {
            try {
                const data = await fetchCreditBalance();
                setCredits(data.credits_balance);
            } catch (err) {
                console.error('Failed to fetch credits:', err);
                // Fallback to subscription credits when API fails
                const fallbackCredits = subscription?.aiCreditsRemaining ?? subscription?.credits ?? 0;
                setCredits(fallbackCredits);
            }
        };

        loadCredits();
    }, [hasAccess, subscription]);

    // Fetch chat sessions on mount
    useEffect(() => {
        if (!hasAccess) return;

        const loadSessions = async () => {
            setIsLoadingSessions(true);
            try {
                const data = await fetchMarketingSessions();
                setChatSessions(data.sessions || []);
            } catch (err) {
                console.error('Failed to fetch chat sessions:', err);
                // Silently fail - user can still use the chat
            } finally {
                setIsLoadingSessions(false);
            }
        };

        loadSessions();
    }, [hasAccess]);

    // Load a previous chat session
    const handleLoadSession = useCallback(async (sessionIdToLoad) => {
        setIsLoadingSession(true);
        try {
            const data = await loadMarketingSession(sessionIdToLoad);
            // Convert API messages to our format
            const loadedMessages = (data.messages || []).map(msg => ({
                role: msg.role,
                content: msg.content,
                timestamp: msg.timestamp || msg.created_at
            }));
            setMessages(loadedMessages);
            setSessionId(sessionIdToLoad);
            setStreamingResponse('');
            setError(null);
            setIsHistorySidebarOpen(false); // Close sidebar after loading
        } catch (err) {
            console.error('Failed to load session:', err);
            setError('Failed to load conversation. Please try again.');
        } finally {
            setIsLoadingSession(false);
        }
    }, []);

    // Delete a chat session
    const handleDeleteSession = useCallback(async (sessionIdToDelete, e) => {
        e.stopPropagation(); // Prevent triggering load
        setDeletingSessionId(sessionIdToDelete);
        try {
            await deleteMarketingSession(sessionIdToDelete);
            setChatSessions(prev => prev.filter(s => s.session_id !== sessionIdToDelete));
            // If we deleted the current session, start a new one
            if (sessionIdToDelete === sessionId) {
                setMessages([]);
                setSessionId(generateSessionId(selectedSkills));
            }
        } catch (err) {
            console.error('Failed to delete session:', err);
            setError('Failed to delete conversation.');
        } finally {
            setDeletingSessionId(null);
        }
    }, [sessionId, selectedSkills]);

    // Refresh sessions list
    const refreshSessions = useCallback(async () => {
        setIsLoadingSessions(true);
        try {
            const data = await fetchMarketingSessions();
            setChatSessions(data.sessions || []);
        } catch (err) {
            console.error('Failed to refresh sessions:', err);
        } finally {
            setIsLoadingSessions(false);
        }
    }, []);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, streamingResponse]);

    // Reset session when skills change
    useEffect(() => {
        setSessionId(generateSessionId(selectedSkills));
    }, [selectedSkills]);

    // Add streaming response to messages when complete
    useEffect(() => {
        if (!isStreaming && streamingResponse) {
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: streamingResponse,
                timestamp: new Date().toISOString()
            }]);
            setStreamingResponse('');
        }
    }, [isStreaming, streamingResponse]);

    // Handle sending message - defined as useCallback before any conditional returns
    const handleSendMessage = useCallback(async () => {
        if (!inputMessage.trim() || isStreaming) return;

        // Check if user has enough credits before sending
        if (credits !== null && credits < CREDITS_PER_MARKETING_CHAT) {
            setInsufficientCreditsInfo({
                required: CREDITS_PER_MARKETING_CHAT,
                available: credits,
            });
            setShowInsufficientCredits(true);
            return;
        }

        const userMessage = inputMessage.trim();
        setInputMessage('');
        setError(null);
        setLastCreditDeduction(null);
        setStreamingResponse('');
        setHasResponseStarted(false);
        hasResponseStartedRef.current = false;

        // Add simulated thinking steps for better UX while backend processes
        setThinkingSteps([
            { stepId: 'sim-1', title: 'Understanding your request', status: 'in_progress' },
        ]);

        // Set streaming AFTER setting thinking steps
        setIsStreaming(true);

        // Simulate step progression
        const timer1 = setTimeout(() => {
            setThinkingSteps([
                { stepId: 'sim-1', title: 'Understanding your request', status: 'completed' },
                { stepId: 'sim-2', title: 'Researching strategies', status: 'in_progress' },
            ]);
        }, 1000);

        const timer2 = setTimeout(() => {
            setThinkingSteps([
                { stepId: 'sim-1', title: 'Understanding your request', status: 'completed' },
                { stepId: 'sim-2', title: 'Researching strategies', status: 'completed' },
                { stepId: 'sim-3', title: 'Generating content', status: 'in_progress' },
            ]);
        }, 2000);

        // Add user message
        setMessages(prev => [...prev, {
            role: 'user',
            content: userMessage,
            timestamp: new Date().toISOString()
        }]);

        try {
            await sendMarketingMessage({
                message: userMessage,
                sessionId,
                skillIds: selectedSkills,
                additionalInstructions: '',
                onCreditInfo: ({ creditsCharged, creditsRemaining }) => {
                    setCredits(creditsRemaining);
                    setLastCreditDeduction(creditsCharged);
                    // Clear deduction indicator after 3 seconds
                    setTimeout(() => setLastCreditDeduction(null), 3000);
                },
                onThinkingStep: (step) => {
                    setThinkingSteps((prev) => {
                        const existing = prev.find((s) => s.stepId === step.stepId);
                        if (existing) {
                            // Update existing step (e.g., in_progress → completed)
                            return prev.map((s) =>
                                s.stepId === step.stepId
                                    ? { ...s, ...step }
                                    : s
                            );
                        }
                        // Add new step
                        return [...prev, {
                            stepId: step.stepId || `step-${prev.length}`,
                            title: step.title,
                            status: step.status || 'in_progress',
                            items: step.items || []
                        }];
                    });
                },
                onChunk: (content) => {
                    if (!hasResponseStartedRef.current) {
                        hasResponseStartedRef.current = true;
                        setHasResponseStarted(true);
                    }
                    setStreamingResponse(prev => prev + content);
                },
                onToolCall: (event) => {
                    setThinkingSteps(prev => [...prev, {
                        stepId: `tool-${event.name}-${Date.now()}`,
                        title: `Using ${event.name}...`,
                        status: 'in_progress',
                        items: []
                    }]);
                },
                onToolResult: (event) => {
                    setThinkingSteps(prev => prev.map(s =>
                        s.title?.includes(event.name)
                            ? { ...s, status: 'completed' }
                            : s
                    ));
                },
                onDone: () => {
                    setIsStreaming(false);
                    // Clear thinking steps after a short delay
                    setTimeout(() => setThinkingSteps([]), 500);
                    // Refresh sessions to update the history list
                    refreshSessions();
                },
                onInsufficientCredits: (info) => {
                    setInsufficientCreditsInfo(info);
                    setShowInsufficientCredits(true);
                    setIsStreaming(false);
                    setThinkingSteps([]);
                },
                onError: (err) => {
                    if (err.message !== 'INSUFFICIENT_CREDITS') {
                        setError(err.message);
                    }
                    setIsStreaming(false);
                    setThinkingSteps([]);
                }
            });
        } catch (err) {
            if (err.message === 'INSUFFICIENT_CREDITS') {
                // Already handled by onInsufficientCredits
            } else if (err.message === 'SUBSCRIPTION_REQUIRED') {
                setError('Marketing Assistant requires an Ultra or Enterprise subscription.');
            } else if (err.message === 'SERVICE_UNAVAILABLE') {
                setError('Service temporarily unavailable. Please try again later.');
            } else {
                setError('Failed to send message. Please try again.');
            }
            setIsStreaming(false);
        }
    }, [inputMessage, isStreaming, sessionId, selectedSkills, credits, refreshSessions]);

    // Show upgrade prompt if no access
    if (!hasAccess) {
        return <UpgradePrompt onUpgrade={onUpgradePlan} />;
    }

    // Show loading state
    if (isLoadingSkills && hasAccess) {
        return (
            <div className="flex items-center justify-center h-full bg-[#f7f4ee] dark:bg-slate-950">
                <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-violet-500 mx-auto mb-4" />
                    <p className="text-slate-600 dark:text-slate-400">Loading marketing skills...</p>
                </div>
            </div>
        );
    }

    const handleCategoryClick = (categoryId) => {
        if (selectedCategory === categoryId) {
            setSelectedCategory(null);
        } else {
            setSelectedCategory(categoryId);
        }
    };

    const handleSkillToggle = (skillId) => {
        setSelectedSkills(prev =>
            prev.includes(skillId)
                ? prev.filter(id => id !== skillId)
                : [...prev, skillId]
        );
    };

    const handleSelectAllInCategory = (categoryId) => {
        const categorySkills = skillsByCategory?.[categoryId] || [];
        const categorySkillIds = categorySkills.map(s => s.id);
        const allSelected = categorySkillIds.every(id => selectedSkills.includes(id));

        if (allSelected) {
            setSelectedSkills(prev => prev.filter(id => !categorySkillIds.includes(id)));
        } else {
            setSelectedSkills(prev => [...new Set([...prev, ...categorySkillIds])]);
        }
    };

    const getSkillById = (skillId) => {
        if (!skillsByCategory) return null;
        for (const [, skills] of Object.entries(skillsByCategory)) {
            const skill = skills.find(s => s.id === skillId);
            if (skill) return skill;
        }
        return null;
    };

    const getSkillCategory = (skillId) => {
        if (!skillsByCategory) return null;
        for (const [categoryId, skills] of Object.entries(skillsByCategory)) {
            if (skills.find(s => s.id === skillId)) {
                return categoryId;
            }
        }
        return null;
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleNewConversation = () => {
        setMessages([]);
        setStreamingResponse('');
        setSessionId(generateSessionId(selectedSkills));
        setError(null);
        // Refresh sessions list to include the previous conversation if it had messages
        refreshSessions();
    };

    const categories = skillsByCategory ? Object.keys(skillsByCategory) : [];

    // Format session date for display
    const formatSessionDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const now = new Date();
        const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Get session title from first user message or default
    const getSessionTitle = (session) => {
        if (session.title) return session.title;
        if (session.preview) return session.preview.slice(0, 40) + (session.preview.length > 40 ? '...' : '');
        return 'Conversation';
    };

    return (
        <div className="flex h-full bg-[#f7f4ee] dark:bg-slate-950">
            {/* Chat History Sidebar */}
            <AnimatePresence>
                {isHistorySidebarOpen && (
                    <motion.div
                        initial={{ width: 0, opacity: 0 }}
                        animate={{ width: 280, opacity: 1 }}
                        exit={{ width: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="h-full border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col overflow-hidden"
                    >
                        {/* Sidebar Header */}
                        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <History className="w-5 h-5 text-violet-500" />
                                <h3 className="font-semibold text-slate-900 dark:text-white">History</h3>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={refreshSessions}
                                    disabled={isLoadingSessions}
                                    className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                                    title="Refresh"
                                >
                                    <RefreshCw className={`w-4 h-4 ${isLoadingSessions ? 'animate-spin' : ''}`} />
                                </button>
                                <button
                                    onClick={() => setIsHistorySidebarOpen(false)}
                                    className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* New Chat Button */}
                        <div className="p-3 border-b border-slate-200 dark:border-slate-800 flex-shrink-0">
                            <button
                                onClick={handleNewConversation}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-violet-500 to-pink-500 text-white font-medium rounded-lg hover:from-violet-600 hover:to-pink-600 transition-all"
                            >
                                <Plus className="w-4 h-4" />
                                New Chat
                            </button>
                        </div>

                        {/* Sessions List */}
                        <div className="flex-1 overflow-y-auto">
                            {isLoadingSessions ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="w-6 h-6 text-violet-500 animate-spin" />
                                </div>
                            ) : chatSessions.length === 0 ? (
                                <div className="text-center py-8 px-4">
                                    <MessageSquare className="w-10 h-10 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
                                    <p className="text-sm text-slate-500 dark:text-slate-400">No conversations yet</p>
                                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Start chatting to save your history</p>
                                </div>
                            ) : (
                                <div className="py-2">
                                    {chatSessions.map((session) => (
                                        <div
                                            key={session.session_id}
                                            onClick={() => handleLoadSession(session.session_id)}
                                            className={`group mx-2 mb-1 p-3 rounded-lg cursor-pointer transition-colors ${
                                                session.session_id === sessionId
                                                    ? 'bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-800'
                                                    : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                                            }`}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-sm font-medium truncate ${
                                                        session.session_id === sessionId
                                                            ? 'text-violet-700 dark:text-violet-300'
                                                            : 'text-slate-900 dark:text-white'
                                                    }`}>
                                                        {getSessionTitle(session)}
                                                    </p>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                                        {formatSessionDate(session.updated_at || session.created_at)}
                                                        {session.message_count && (
                                                            <span className="ml-2">{session.message_count} messages</span>
                                                        )}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={(e) => handleDeleteSession(session.session_id, e)}
                                                    disabled={deletingSessionId === session.session_id}
                                                    className="p-1 text-slate-400 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    title="Delete conversation"
                                                >
                                                    {deletingSessionId === session.session_id ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <Trash2 className="w-4 h-4" />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col min-w-0 relative">
                {/* Compact Header with Skill Selection */}
                <div className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                {/* Header Row */}
                <div className="px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {/* History Toggle Button */}
                        <button
                            onClick={() => setIsHistorySidebarOpen(!isHistorySidebarOpen)}
                            className={`p-2 rounded-lg transition-colors ${
                                isHistorySidebarOpen
                                    ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400'
                                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                            }`}
                            title={isHistorySidebarOpen ? 'Hide history' : 'Show history'}
                        >
                            {isHistorySidebarOpen ? <ChevronLeft className="w-5 h-5" /> : <History className="w-5 h-5" />}
                        </button>
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="font-semibold text-slate-900 dark:text-white text-sm">Marketing Assistant</h2>
                            <p className="text-xs text-slate-500">
                                {selectedSkills.length > 0
                                    ? `${selectedSkills.length} skills selected`
                                    : 'Select skills or ask anything'
                                }
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Marketing Brief Button */}
                        <button
                            onClick={() => setIsBriefEditorOpen(true)}
                            className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-xs font-medium transition-colors"
                            title="Edit Marketing Brief"
                        >
                            <FileText className="w-3.5 h-3.5" />
                            <span>Brief</span>
                        </button>
                        {/* Credit Balance Display */}
                        {credits !== null && (
                            <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium ${
                                credits <= CREDIT_THRESHOLDS.CRITICAL
                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                    : credits <= CREDIT_THRESHOLDS.LOW
                                        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                            }`}>
                                <Zap className="w-3.5 h-3.5" />
                                <span>{formatCredits(credits)} credits</span>
                                {lastCreditDeduction && (
                                    <span className="text-red-500 dark:text-red-400 animate-pulse">
                                        -{lastCreditDeduction}
                                    </span>
                                )}
                            </div>
                        )}
                        {/* Show selected skills count when collapsed */}
                        {!isSkillPanelExpanded && selectedSkills.length > 0 && (
                            <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
                                <span className="text-xs font-medium text-violet-700 dark:text-violet-300">
                                    {selectedSkills.length} skill{selectedSkills.length > 1 ? 's' : ''}
                                </span>
                                <button
                                    onClick={() => setSelectedSkills([])}
                                    className="text-violet-500 hover:text-violet-700 dark:hover:text-violet-200"
                                    title="Clear all"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}
                        {messages.length > 0 && (
                            <button
                                onClick={handleNewConversation}
                                className="p-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                title="New conversation"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </button>
                        )}
                        <button
                            onClick={() => setIsSkillPanelExpanded(!isSkillPanelExpanded)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                                isSkillPanelExpanded
                                    ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'
                                    : selectedSkills.length > 0
                                        ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-900/30'
                                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                            }`}
                        >
                            Skills
                            {selectedSkills.length > 0 && (
                                <span className="w-5 h-5 bg-violet-500 text-white text-xs rounded-full flex items-center justify-center">
                                    {selectedSkills.length}
                                </span>
                            )}
                            <ChevronDown className={`w-4 h-4 transition-transform ${isSkillPanelExpanded ? 'rotate-180' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* Expandable Skills Panel */}
                <AnimatePresence>
                    {isSkillPanelExpanded && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden border-t border-slate-200 dark:border-slate-800"
                        >
                            <div className="p-4">
                                {skillsError && (
                                    <div className="mb-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4" />
                                        {skillsError}
                                    </div>
                                )}

                                {/* Category Pills */}
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {categories.map((categoryId) => {
                                        const Icon = CATEGORY_ICONS[categoryId] || Sparkles;
                                        const color = CATEGORY_COLORS[categoryId] || 'sky';
                                        const colors = colorClasses[color];
                                        const skills = skillsByCategory[categoryId] || [];
                                        const selectedCount = skills.filter(s => selectedSkills.includes(s.id)).length;
                                        const isActive = selectedCategory === categoryId;

                                        return (
                                            <button
                                                key={categoryId}
                                                onClick={() => handleCategoryClick(categoryId)}
                                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                                    isActive
                                                        ? `${colors.bgLight} ${colors.text} ring-2 ${colors.ring}`
                                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                                                }`}
                                            >
                                                <Icon className="w-4 h-4" />
                                                {CATEGORY_NAMES[categoryId] || categoryId}
                                                <span className="text-xs opacity-70">({skills.length})</span>
                                                {selectedCount > 0 && (
                                                    <span className="ml-1 w-5 h-5 bg-violet-500 text-white text-xs rounded-full flex items-center justify-center">
                                                        {selectedCount}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Expanded Category Skills */}
                                <AnimatePresence>
                                    {selectedCategory && skillsByCategory[selectedCategory] && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                        {CATEGORY_NAMES[selectedCategory]} Skills
                                                    </span>
                                                    <button
                                                        onClick={() => handleSelectAllInCategory(selectedCategory)}
                                                        className="text-xs text-violet-600 dark:text-violet-400 hover:underline"
                                                    >
                                                        {skillsByCategory[selectedCategory].every(s => selectedSkills.includes(s.id))
                                                            ? 'Deselect all'
                                                            : 'Select all'
                                                        }
                                                    </button>
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {skillsByCategory[selectedCategory].map((skill) => {
                                                        const isSelected = selectedSkills.includes(skill.id);
                                                        const color = CATEGORY_COLORS[selectedCategory] || 'sky';
                                                        const colors = colorClasses[color];

                                                        return (
                                                            <button
                                                                key={skill.id}
                                                                onClick={() => handleSkillToggle(skill.id)}
                                                                className={`px-3 py-1.5 rounded-lg text-sm transition-all flex items-center gap-2 ${
                                                                    isSelected
                                                                        ? `${colors.bgLight} ${colors.text} ${colors.border} border`
                                                                        : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-transparent hover:border-slate-300 dark:hover:border-slate-600'
                                                                }`}
                                                                title={skill.description}
                                                            >
                                                                {isSelected && <CheckCircle className="w-3.5 h-3.5" />}
                                                                {skill.name}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Selected Skills Summary */}
                                {selectedSkills.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                Selected ({selectedSkills.length})
                                            </span>
                                            <button
                                                onClick={() => setSelectedSkills([])}
                                                className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                                            >
                                                Clear all
                                            </button>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {selectedSkills.map(skillId => {
                                                const skill = getSkillById(skillId);
                                                const categoryId = getSkillCategory(skillId);
                                                const color = CATEGORY_COLORS[categoryId] || 'sky';
                                                const colors = colorClasses[color];

                                                return (
                                                    <span
                                                        key={skillId}
                                                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${colors.bgLight} ${colors.text}`}
                                                    >
                                                        {skill?.name || skillId}
                                                        <button
                                                            onClick={() => handleSkillToggle(skillId)}
                                                            className="hover:opacity-70"
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-auto p-4">
                {messages.length === 0 && !streamingResponse ? (
                    <div className="h-full flex flex-col items-center justify-center text-center px-4">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500/20 to-pink-500/20 flex items-center justify-center mb-4">
                            <Sparkles className="w-8 h-8 text-violet-500" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                            How can I help with your marketing?
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mb-4">
                            {selectedSkills.length > 0
                                ? `I'll use ${selectedSkills.length} selected skill${selectedSkills.length > 1 ? 's' : ''} to give you focused guidance.`
                                : 'Select specific skills above for focused guidance, or ask me anything about marketing.'
                            }
                        </p>
                        {/* Marketing Brief CTA */}
                        <button
                            onClick={() => setIsBriefEditorOpen(true)}
                            className="flex items-center gap-2 px-4 py-2 mb-6 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg text-sm text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/30 transition-colors"
                        >
                            <FileText className="w-4 h-4" />
                            <span>Set up your Marketing Brief for personalized advice</span>
                        </button>
                        <div className="flex flex-wrap justify-center gap-2 max-w-lg">
                            {[
                                'How do I improve my SEO?',
                                'Write a launch strategy',
                                'Optimize my landing page',
                                'Create an email sequence'
                            ].map((suggestion) => (
                                <button
                                    key={suggestion}
                                    onClick={() => setInputMessage(suggestion)}
                                    className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-600 dark:text-slate-400 hover:border-violet-300 dark:hover:border-violet-600 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                                >
                                    {suggestion}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="max-w-3xl mx-auto space-y-4">
                        {messages.map((message, index) => (
                            <div
                                key={index}
                                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : ''}`}
                            >
                                {message.role === 'assistant' && (
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                                        <Bot className="w-4 h-4 text-white" />
                                    </div>
                                )}
                                <div
                                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                                        message.role === 'user'
                                            ? 'bg-violet-600 text-white'
                                            : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700'
                                    }`}
                                >
                                    <div className="text-sm">
                                        {message.role === 'assistant' ? formatMessageContent(message.content) : message.content}
                                    </div>
                                    {message.role === 'assistant' && (
                                        <div className="flex justify-end mt-2">
                                            <button
                                                onClick={() => handleCopyMessage(index, message.content)}
                                                className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                                                title="Copy to clipboard"
                                            >
                                                {copiedMessageIndex === index ? (
                                                    <>
                                                        <Check className="w-3.5 h-3.5 text-emerald-500" />
                                                        <span className="text-emerald-500">Copied!</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy className="w-3.5 h-3.5" />
                                                        <span>Copy</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {message.role === 'user' && (
                                    <div className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                                        <User className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Streaming Response */}
                        {(isStreaming || streamingResponse) && (
                            <div className="flex gap-3">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                                    <Bot className="w-4 h-4 text-white" />
                                </div>
                                <div className="max-w-[80%]">
                                    {/* AI Reasoning Steps - Always show when streaming */}
                                    {isStreaming && !streamingResponse && (
                                        <AIReasoningPanel
                                            steps={thinkingSteps.length > 0 ? thinkingSteps : [
                                                { stepId: 'default-1', title: 'Understanding your request', status: 'in_progress' }
                                            ]}
                                            isCollapsed={hasResponseStarted}
                                            accentColor="violet"
                                        />
                                    )}
                                    {thinkingSteps.length > 0 && streamingResponse && (
                                        <AIReasoningPanel steps={thinkingSteps} isCollapsed={true} accentColor="violet" />
                                    )}

                                    {/* Response content */}
                                    <div className="rounded-2xl px-4 py-3 bg-white dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700">
                                        {streamingResponse && (
                                            <div className="text-sm">{formatMessageContent(streamingResponse)}</div>
                                        )}
                                        {isStreaming && !streamingResponse && (
                                            <div className="flex items-center gap-1">
                                                <span className="w-2 h-2 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                                <span className="w-2 h-2 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                                <span className="w-2 h-2 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>
                )}
            </div>

            {/* Low Credit Warning */}
            {credits !== null && credits <= CREDIT_THRESHOLDS.LOW && credits > 0 && !error && (
                <div className={`px-4 py-2 border-t ${
                    credits <= CREDIT_THRESHOLDS.CRITICAL
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                }`}>
                    <div className="max-w-3xl mx-auto flex items-center gap-2 text-sm">
                        <AlertCircle className={`w-4 h-4 ${
                            credits <= CREDIT_THRESHOLDS.CRITICAL
                                ? 'text-red-500'
                                : 'text-amber-500'
                        }`} />
                        <span className={credits <= CREDIT_THRESHOLDS.CRITICAL ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}>
                            Low credits: {credits} remaining (~{getEstimatedChatsRemaining(credits, 'marketing')} chats)
                        </span>
                        <button
                            onClick={onUpgradePlan}
                            className={`ml-auto text-xs font-medium px-2 py-1 rounded ${
                                credits <= CREDIT_THRESHOLDS.CRITICAL
                                    ? 'bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-700'
                                    : 'bg-amber-100 dark:bg-amber-800 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-700'
                            }`}
                        >
                            Buy Credits
                        </button>
                    </div>
                </div>
            )}

            {/* Error Banner */}
            {error && (
                <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
                    <div className="max-w-3xl mx-auto flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                        <button
                            onClick={() => setError(null)}
                            className="ml-auto hover:opacity-70"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Input Area */}
            <div className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                <div className="max-w-3xl mx-auto">
                    <div className="flex items-end gap-3">
                        <div className="flex-1 relative">
                            <textarea
                                ref={inputRef}
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder={selectedSkills.length > 0
                                    ? `Ask about ${selectedSkills.length} selected skill${selectedSkills.length > 1 ? 's' : ''}...`
                                    : 'Ask anything about marketing...'
                                }
                                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                                rows={1}
                                style={{ minHeight: '48px', maxHeight: '120px' }}
                                disabled={isStreaming}
                            />
                        </div>
                        <button
                            onClick={handleSendMessage}
                            disabled={!inputMessage.trim() || isStreaming}
                            className="p-3 bg-gradient-to-r from-violet-500 to-pink-500 text-white rounded-xl hover:from-violet-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isStreaming ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <Send className="w-5 h-5" />
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-slate-400 mt-2 text-center">
                        Press Enter to send, Shift+Enter for new line
                    </p>
                </div>
            </div>

                {/* Insufficient Credits Modal */}
                <AnimatePresence>
                    {showInsufficientCredits && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                            onClick={() => setShowInsufficientCredits(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.95, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.95, opacity: 0 }}
                                className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl max-w-md w-full p-6"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="text-center">
                                    <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                                        <AlertCircle className="w-8 h-8 text-red-500" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                                        Insufficient Credits
                                    </h3>
                                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                                        You need <span className="font-semibold">{insufficientCreditsInfo?.required || CREDITS_PER_MARKETING_CHAT}</span> credits to send a message,
                                        but you only have <span className="font-semibold">{insufficientCreditsInfo?.available || 0}</span> credits remaining.
                                    </p>
                                    <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 mb-6">
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Credit cost per message</p>
                                        <p className="text-2xl font-bold text-slate-900 dark:text-white">{CREDITS_PER_MARKETING_CHAT} credits</p>
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => setShowInsufficientCredits(false)}
                                            className="flex-1 px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-xl font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowInsufficientCredits(false);
                                                onUpgradePlan?.();
                                            }}
                                            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-violet-500 to-pink-500 text-white rounded-xl font-medium hover:from-violet-600 hover:to-pink-600 transition-colors flex items-center justify-center gap-2"
                                        >
                                            <Zap className="w-4 h-4" />
                                            Buy Credits
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Loading overlay when loading a session */}
                {isLoadingSession && (
                    <div className="absolute inset-0 bg-white/80 dark:bg-slate-950/80 flex items-center justify-center z-40">
                        <div className="text-center">
                            <Loader2 className="w-8 h-8 text-violet-500 animate-spin mx-auto mb-3" />
                            <p className="text-sm text-slate-600 dark:text-slate-400">Loading conversation...</p>
                        </div>
                    </div>
                )}

                {/* Marketing Brief Editor Modal */}
                <AnimatePresence>
                    {isBriefEditorOpen && (
                        <MarketingBriefEditor
                            isOpen={isBriefEditorOpen}
                            onClose={() => setIsBriefEditorOpen(false)}
                        />
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default MarketingAgent;
